import { ExportJobStatus } from "@/shared/domain/exportJobs/ExportJobRecord";
import { FileService } from "@/shared/domain/files/FileService";
import { RobotoDomainException } from "@/shared/http/exceptions";

import { UIExportJob } from "./types";

const TERMINAL = new Set<ExportJobStatus>(["completed", "failed", "expired"]);
const POLL_INTERVAL_MS = 2000;
const START_FAILED_MESSAGE = "Failed to start the download. Please try again.";

type Listener = () => void;

export interface ExportSnapshot {
  jobs: UIExportJob[];
  isToastVisible: boolean;
}

/**
 * Manages async ZIP export jobs: polling, state transitions, and download
 * triggering. Plain TypeScript class — no React dependency — so it can be
 * tested without a component tree.
 *
 * Implements the useSyncExternalStore contract via `subscribe` / `getSnapshot`.
 */
export class FileExportService {
  #getFileService: () => FileService;
  #onDownload: (url: string) => void;
  #onStartFailed: (error: unknown) => void;
  #jobs: Map<string, UIExportJob> = new Map();
  #intervals: Map<string, ReturnType<typeof setInterval>> = new Map();
  // One controller per export, keyed like #jobs. Every request an export
  // makes carries its signal, so destroy() can cancel the work in flight
  // rather than only refusing to start more.
  #controllers: Map<string, AbortController> = new Map();
  #downloadTriggered: Set<string> = new Set();
  #listeners: Set<Listener> = new Set();
  #isToastVisible = false;
  #snapshot: ExportSnapshot = { jobs: [], isToastVisible: false };
  #nextPlaceholder = 0;

  /**
   * @param getFileService - Resolves the file service at call time, so polling
   *   uses the current HTTP client.
   * @param onDownload - Opens a finished export's download URL.
   * @param onStartFailed - Hears about an export the server refused to create,
   *   for error reporting; the toast shows the person the reason either way.
   */
  constructor(
    getFileService: () => FileService,
    onDownload: (url: string) => void,
    onStartFailed: (error: unknown) => void = () => {},
  ) {
    this.#getFileService = getFileService;
    this.#onDownload = onDownload;
    this.#onStartFailed = onStartFailed;
  }

  /**
   * Export `nodeIds` (files or directories) as one ZIP, shown in the toast as
   * `label`.
   *
   * The toast opens with the export pending before the server is asked, so
   * the person sees the download start on their click, not a round trip
   * later. Once the server accepts the export, the entry takes the server's
   * job id and polling begins; if the server refuses it, the entry fails with
   * the reason and no polling starts. Exports that already finished leave the
   * toast, so it shows only this one and any still in flight.
   */
  startExport(nodeIds: string[], label: string): void {
    for (const [id, job] of this.#jobs) {
      if (TERMINAL.has(job.status as ExportJobStatus)) {
        this.#forget(id);
      }
    }

    // The id is replaced by the server's once the export is accepted; until
    // then it only has to be unique within this toast.
    const placeholderId = `pending-export-${++this.#nextPlaceholder}`;
    this.#jobs.set(placeholderId, {
      id: placeholderId,
      label,
      status: "pending",
      bytesUploaded: 0,
      totalBytes: 0,
      created: Date.now(),
    });
    this.#controllers.set(placeholderId, new AbortController());
    this.#isToastVisible = true;
    this.#commit();

    void this.#create(placeholderId, nodeIds);
  }

  async #create(placeholderId: string, nodeIds: string[]): Promise<void> {
    const placeholder = this.#jobs.get(placeholderId);
    const controller = this.#controllers.get(placeholderId);
    if (!placeholder || !controller) {
      return;
    }

    try {
      const { export_job_id: jobId } =
        await this.#getFileService().createExportJob(nodeIds, {
          abortSignal: controller.signal,
        });
      // The abort can land between the response arriving and this line
      // running; the request then resolves instead of rejecting.
      if (controller.signal.aborted) {
        this.#forget(placeholderId);
      } else {
        this.#jobs.delete(placeholderId);
        this.#controllers.delete(placeholderId);
        this.#jobs.set(jobId, { ...placeholder, id: jobId });
        this.#controllers.set(jobId, controller);
        this.#startPolling(jobId);
      }
    } catch (error) {
      // Judged by the signal, not the error's shape: fetch rejects an abort
      // with an AbortError, but the HTTP client's retry policy raises its own
      // cancellation error when the abort lands during a retry wait.
      if (controller.signal.aborted) {
        // destroy() cancelled the export: the provider is gone, and so is
        // the toast that would have shown it.
        this.#forget(placeholderId);
      } else {
        this.#onStartFailed(error);
        // The person may have closed the toast while the server was
        // answering, trusting the ZIP to arrive on its own; the refusal has
        // to be seen.
        this.#isToastVisible = true;
        this.#jobs.set(placeholderId, {
          ...placeholder,
          status: "failed",
          error:
            error instanceof RobotoDomainException && error.isClientError()
              ? error.message
              : START_FAILED_MESSAGE,
        });
      }
    }

    this.#commit();
  }

  /** Fire an immediate first poll, then keep going at the regular interval. */
  #startPolling(jobId: string): void {
    void this.#poll(jobId);
    const interval = setInterval(
      () => void this.#poll(jobId),
      POLL_INTERVAL_MS,
    );
    this.#intervals.set(jobId, interval);
  }

  /** Hide the toast without affecting job state or polling. */
  closeToast(): void {
    this.#isToastVisible = false;
    this.#commit();
  }

  /** True if any job has not yet reached a terminal status. */
  hasActiveJobs(): boolean {
    for (const job of this.#jobs.values()) {
      if (!TERMINAL.has(job.status as ExportJobStatus)) {
        return true;
      }
    }
    return false;
  }

  /** useSyncExternalStore — subscribe */
  subscribe = (listener: Listener): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  /** useSyncExternalStore — getSnapshot */
  getSnapshot = (): ExportSnapshot => this.#snapshot;

  /**
   * Cancel every export in flight and stop all polling. Call on provider
   * unmount or logout. Exports started afterwards run normally, since React
   * StrictMode runs the provider's effect cleanup once at mount and the
   * service lives on.
   */
  destroy(): void {
    for (const interval of this.#intervals.values()) {
      clearInterval(interval);
    }
    this.#intervals.clear();
    for (const controller of this.#controllers.values()) {
      controller.abort();
    }
    this.#controllers.clear();
  }

  async #poll(jobId: string): Promise<void> {
    const existing = this.#jobs.get(jobId);
    const signal = this.#controllers.get(jobId)?.signal;
    if (!existing || !signal) {
      return;
    }

    try {
      const data = await this.#getFileService().getExportJob(jobId, {
        abortSignal: signal,
      });
      if (signal.aborted) {
        return;
      }
      const updated: UIExportJob = {
        ...existing,
        status: data.status,
        bytesUploaded: data.bytes_uploaded ?? existing.bytesUploaded,
        totalBytes: data.total_bytes ?? existing.totalBytes,
        downloadUrl: data.download_url,
        error: data.error,
      };
      this.#jobs.set(jobId, updated);

      if (TERMINAL.has(data.status)) {
        this.#stopPolling(jobId);
        if (
          data.status === "completed" &&
          data.download_url &&
          !this.#downloadTriggered.has(jobId)
        ) {
          this.#downloadTriggered.add(jobId);
          this.#onDownload(data.download_url);
        }
      }
    } catch (err) {
      if (signal.aborted) {
        // destroy() stopped this poll; the job did not fail.
        return;
      }
      // HttpClient already retries transiently; a thrown error means retries
      // are exhausted — surface as a failed job and stop polling.
      this.#stopPolling(jobId);
      this.#jobs.set(jobId, {
        ...existing,
        status: "failed",
        error: err instanceof Error ? err.message : "Unknown error",
      });
    }

    this.#commit();
  }

  /** The job is settled: no more requests on its behalf. */
  #stopPolling(jobId: string): void {
    clearInterval(this.#intervals.get(jobId));
    this.#intervals.delete(jobId);
    this.#controllers.delete(jobId);
  }

  /** Remove every trace of a job from the service. */
  #forget(jobId: string): void {
    this.#stopPolling(jobId);
    this.#jobs.delete(jobId);
  }

  /** Recompute the snapshot and notify listeners only if something changed. */
  #commit(): void {
    const nextJobs = Array.from(this.#jobs.values()).sort(
      (a, b) => a.created - b.created,
    );
    const nextVisible = this.#isToastVisible;
    const prev = this.#snapshot;

    const unchanged =
      prev.isToastVisible === nextVisible &&
      prev.jobs.length === nextJobs.length &&
      prev.jobs.every(
        (j, i) =>
          j.id === nextJobs[i].id &&
          j.status === nextJobs[i].status &&
          j.bytesUploaded === nextJobs[i].bytesUploaded &&
          j.totalBytes === nextJobs[i].totalBytes,
      );

    if (unchanged) {
      return;
    }

    this.#snapshot = { jobs: nextJobs, isToastVisible: nextVisible };
    this.#listeners.forEach((l) => l());
  }
}
